<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { actOnLeave, adjustBalance, applyLeave, loadLedger, loadOverview, loadRequests, readNotice, responseError, saveEmployee, saveHoliday, setEmployeeStatus, loadEmployeeHistory } from '@/api/entities/hr/leave'
import type { Employee, EmployeeInput, EmployeeAudit, LeavePeriod, Entry, LeaveRequest, Overview, Page } from '@/api/entities/hr/leave'
import type { ApiResponse } from '@/api'

definePage({ meta: { action: 'read', subject: 'Auth' } })

const overview = ref<Overview | null>(null)
const loading = ref(false)
const busy = ref(false)
const error = ref('')
const success = ref('')
const scope = ref('mine')
const page = ref(1)
const requests = ref<Page<LeaveRequest>>({ data: [], current_page: 1, last_page: 1, total: 0 })
const mine = computed(() => overview.value?.employee)
const form = ref({ category: 'annual', starts_on: '', ends_on: '', kind: 'annual', reason: '' })
const statusNames: Record<string, string> = { pending: '승인 대기', approved: '승인', rejected: '반려', cancelled: '취소', cancellation_pending: '취소 승인 대기', expired: '신청 기간 경과' }
const kindNames: Record<string, string> = { annual: '연차', am: '오전 반차', pm: '오후 반차' }
const entryNames: Record<string, string> = { opening: '기준 잔액', grant: '연차 부여', use: '사용', restore: '취소 복원', adjustment: '조정', migration_use: '이관 사용분', migration_adjustment: '이관 잔액 조정' }
const categories = [{ title: '연차', value: 'annual' }, { title: '여름휴가', value: 'summer' }]
const kinds = [{ title: '종일', value: 'annual' }, { title: '오전 반차', value: 'am' }, { title: '오후 반차', value: 'pm' }]
const outsideSummerSeason = computed(() => Number(overview.value?.today.slice(5, 7)) < 7 || Number(overview.value?.today.slice(5, 7)) > 8)
const summerYear = computed(() => form.value.category === 'summer' && form.value.starts_on ? form.value.starts_on.slice(0, 4) : mine.value?.summer.starts_on.slice(0, 4))

const selectedSummer = computed<LeavePeriod | null>(() => {
  const employee = mine.value
  if (!employee || !summerYear.value)
    return null
  const existing = employee.summer_periods.find(period => period.starts_on.startsWith(`${summerYear.value}-`))
  if (existing)
    return existing

  return { ...employee.summer, id: null, available_from: `${summerYear.value}-07-01`, starts_on: `${summerYear.value}-07-01`, ends_on: `${summerYear.value}-08-31`, granted_days: employee.summer_days, used_days: 0, remaining_days: employee.summer_days, pending_days: 0, available_days: employee.summer_days }
})

const requestMin = computed(() => form.value.category === 'summer' ? [overview.value?.today ?? '', selectedSummer.value?.starts_on ?? ''].sort().at(-1) : mine.value?.period?.available_from)
const requestMax = computed(() => form.value.category === 'summer' ? selectedSummer.value?.ends_on : mine.value?.period?.ends_on)
function changeCategory(category: string) {
  form.value.category = category

  const start = category === 'summer' ? [overview.value?.today ?? '', mine.value?.summer.starts_on ?? ''].sort().at(-1) ?? '' : overview.value?.today ?? ''

  form.value.starts_on = start
  form.value.ends_on = start
}
const employeeDialog = ref(false)
const employeeId = ref<number | null>(null)
const initialized = ref(false)
const blankEmployee = (): EmployeeInput => ({ name: '', user_id: null, approver_user_id: null, hire_date: '', annual_days: null, summer_days: 3, opening_days: null, balance_date: null, active: false })
const employeeForm = ref<EmployeeInput>(blankEmployee())
const showArchived = ref(false)
const visibleEmployees = computed(() => overview.value?.employees.filter(employee => showArchived.value || !employee.archived_at) ?? [])
const statusDialog = ref(false)
const statusEmployee = ref<Employee | null>(null)
const statusReason = ref('')
const employeeHistoryDialog = ref(false)
const historyEmployee = ref<Employee | null>(null)
const historyPage = ref(1)
const employeeHistory = ref<Page<EmployeeAudit>>({ data: [], current_page: 1, last_page: 1, total: 0 })
const auditNames: Record<string, string> = { create: '추가', update: '수정', archive: '비활성화', restore: '준비 상태 복원', import: '초기 연차 이관' }
const fieldNames: Record<string, string> = { name: '이름', user_id: '계정 ID', approver_user_id: '승인자 ID', hire_date: '입사일', summer_units: '새 여름 기간 부여일수', annual_units: '다음 부여 일수', opening_units: '초기 잔액', opening_annual_units: '이관 당기 부여', annual_policy: '향후 부여 방식', balance_date: '잔액 기준일', active: '신청 활성화', archived_at: '비활성화 시각' }
function formatAuditValue(field: string, value: unknown): string {
  if (value === null || value === undefined)
    return '미설정'
  if (field.endsWith('_units'))
    return `${Number(value) / 2}일`
  if (field === 'annual_policy')
    return value === 'statutory_eligible' ? '승인된 근속가산 산식' : '수동 설정'
  if (field === 'hire_date' || field === 'balance_date')
    return String(value).slice(0, 10)
  if (field === 'active')
    return value ? '활성' : '비활성'

  return String(value)
}
function changedAuditFields(audit: EmployeeAudit) {
  return Object.entries(audit.after_values).filter(([key, value]) => !audit.before_values || value !== audit.before_values[key])
}
const accountItems = computed(() => overview.value?.users.map(user => ({ title: `${user.name} (${user.email})`, value: user.id })) ?? [])
const decisionDialog = ref(false)
const selectedRequest = ref<LeaveRequest | null>(null)
const selectedAction = ref('')
const decisionNote = ref('')
const actionNames: Record<string, string> = { approve: '승인', reject: '반려', cancel: '취소 요청', approve_cancel: '취소 승인', reject_cancel: '취소 반려' }
const adjustmentDialog = ref(false)
const adjustmentEmployee = ref<Employee | null>(null)
const adjustmentDays = ref(0)
const adjustmentNote = ref('')
const holidayDate = ref('')
const holidayName = ref('')
const ledgerCategory = ref<'annual' | 'summer'>('annual')
const ledgerDialog = ref(false)
const ledgerEmployee = ref<Employee | null>(null)
const ledgerPage = ref(1)
const ledger = ref<Page<Entry>>({ data: [], current_page: 1, last_page: 1, total: 0 })

async function refreshRequests() {
  const result = await loadRequests(scope.value, page.value)
  if (result.success && result.data)
    requests.value = result.data
  else
    error.value = responseError(result)
}

async function refresh() {
  loading.value = true

  const result = await loadOverview()
  if (result.success && result.data) {
    overview.value = result.data
    if (!form.value.starts_on) {
      form.value.starts_on = result.data.today
      form.value.ends_on = result.data.today
    }
    await refreshRequests()
  }
  else {
    error.value = responseError(result)
  }
  loading.value = false
}

async function mutate(task: () => Promise<ApiResponse<unknown>>, done: () => void, message: string) {
  if (busy.value)
    return
  busy.value = true
  error.value = ''
  success.value = ''
  try {
    const result = await task()
    if (!result.success) {
      error.value = responseError(result)

      return
    }
    done()
    success.value = message
    await refresh()
  }
  catch {
    error.value = '연결 상태를 확인한 뒤 다시 시도해 주세요.'
  }
  finally {
    busy.value = false
  }
}

function openEmployee(employee?: Employee) {
  employeeId.value = employee?.id ?? null
  initialized.value = employee?.initialized ?? false
  employeeForm.value = employee ? {
    name: employee.name, user_id: employee.user_id, approver_user_id: employee.approver_user_id,
    hire_date: employee.hire_date, annual_days: employee.annual_days, summer_days: employee.summer_days, opening_days: employee.opening_days,
    balance_date: employee.balance_date, active: employee.active,
  } : { ...blankEmployee(), request_key: crypto.randomUUID() }
  error.value = ''
  employeeDialog.value = true
}

async function submitEmployee() {
  const editing = employeeId.value !== null

  await mutate(() => saveEmployee(employeeId.value, employeeForm.value), () => { employeeDialog.value = false }, editing ? '직원 정보를 수정했습니다.' : '직원을 추가했습니다.')
}

function openStatus(employee: Employee) {
  statusEmployee.value = employee
  statusReason.value = ''
  error.value = ''
  statusDialog.value = true
}

async function confirmStatus() {
  const employee = statusEmployee.value
  if (!employee || !statusReason.value.trim())
    return
  const action = employee.archived_at ? 'restore' : 'archive'

  await mutate(() => setEmployeeStatus(employee.id, action, statusReason.value.trim()), () => { statusDialog.value = false }, action === 'archive' ? '직원을 비활성화했습니다. 이력은 보존됩니다.' : '직원을 준비 상태로 복원했습니다.')
}

async function fetchEmployeeHistory() {
  if (!historyEmployee.value)
    return
  const result = await loadEmployeeHistory(historyEmployee.value.id, historyPage.value)
  if (result.success && result.data)
    employeeHistory.value = result.data
  else
    error.value = responseError(result)
}

async function showEmployeeHistory(employee: Employee) {
  historyEmployee.value = employee
  historyPage.value = 1
  employeeHistory.value = { data: [], current_page: 1, last_page: 1, total: 0 }
  employeeHistoryDialog.value = true
  await fetchEmployeeHistory()
}

function decide(request: LeaveRequest, action: string) {
  selectedRequest.value = request
  selectedAction.value = action
  decisionNote.value = ''
  decisionDialog.value = true
}

async function confirmDecision() {
  const request = selectedRequest.value
  if (!request)
    return
  await mutate(() => actOnLeave(request.id, selectedAction.value, decisionNote.value), () => { decisionDialog.value = false }, '신청 상태를 변경했습니다.')
}

async function changeScope(value: string) {
  scope.value = value
  page.value = 1
  await refreshRequests()
}

function openAdjustment(employee: Employee) {
  adjustmentEmployee.value = employee
  adjustmentDays.value = 0
  adjustmentNote.value = ''
  adjustmentDialog.value = true
}

async function confirmAdjustment() {
  const employee = adjustmentEmployee.value
  if (!employee)
    return
  await mutate(() => adjustBalance(employee.id, Number(adjustmentDays.value), adjustmentNote.value), () => { adjustmentDialog.value = false }, '잔액 조정을 기록했습니다.')
}

async function fetchLedger() {
  if (!ledgerEmployee.value)
    return
  const result = await loadLedger(ledgerEmployee.value.id, ledgerPage.value, ledgerCategory.value)
  if (result.success && result.data)
    ledger.value = result.data
  else
    error.value = responseError(result)
}

async function showLedger(employee: Employee, category: 'annual' | 'summer' = 'annual') {
  ledgerCategory.value = category
  ledgerEmployee.value = employee
  ledgerPage.value = 1
  ledgerDialog.value = true
  await fetchLedger()
}

onMounted(refresh)
</script>

<template>
  <div>
    <div class="d-flex flex-wrap align-center justify-space-between ga-3 mb-4">
      <div>
        <h1 class="text-h5">
          연차 관리
        </h1>
        <p class="text-body-2 mt-1">
          입사일마다 연차 기간을 갱신합니다. 신청 중인 연차는 사용 가능 일수에서 제외됩니다.
        </p>
      </div>
      <VBtn
        variant="tonal"
        :loading="loading"
        @click="refresh"
      >
        새로고침
      </VBtn>
    </div>
    <VAlert
      v-if="error"
      type="error"
      closable
      class="mb-4"
      @click:close="error = ''"
    >
      {{ error }}
    </VAlert>
    <VAlert
      v-if="success"
      type="success"
      closable
      class="mb-4"
      @click:close="success = ''"
    >
      {{ success }}
    </VAlert>
    <VProgressLinear
      v-if="loading"
      indeterminate
      class="mb-4"
    />
    <template v-if="overview">
      <VAlert
        v-for="notice in overview.notices"
        :key="notice.id"
        type="info"
        variant="tonal"
        class="mb-3"
      >
        <div class="d-flex align-center justify-space-between ga-2">
          <span>{{ notice.message }}</span><VBtn
            size="small"
            variant="text"
            :disabled="busy"
            @click="mutate(() => readNotice(notice.id), () => {}, '알림을 확인했습니다.')"
          >
            확인
          </VBtn>
        </div>
      </VAlert>
      <VAlert
        v-if="!mine?.period"
        type="info"
        class="mb-4"
      >
        인사담당자가 계정, 승인자와 잔액 기준일을 확인하면 휴가를 신청할 수 있습니다. 입사 기념일이 지난 뒤에는 새 부여일수 설정이 필요합니다.
      </VAlert>
      <VAlert
        v-if="mine?.period && mine.annual_days === null"
        type="info"
        variant="tonal"
        class="mb-4"
      >
        현재 기간의 잔액으로 신청할 수 있습니다. 다음 입사 기념일에는 새 부여일수가 확인될 때까지 신청이 중지됩니다.
      </VAlert>
      <VCard
        v-if="mine?.period"
        class="mb-4"
        :title="`${mine.name}님의 연차`"
      >
        <VCardText>
          <p class="mb-3">
            {{ mine.period.starts_on }} ~ {{ mine.period.ends_on }} · 매년 {{ mine.hire_date.slice(5) }} 갱신
          </p>
          <div class="d-flex flex-wrap ga-6 mb-3">
            <div>연간 부여 <strong>{{ mine.period.granted_days === null ? '미설정' : `${mine.period.granted_days}일` }}</strong></div>
            <div>사용 <strong>{{ mine.period.used_days === null ? '미확정' : `${mine.period.used_days}일` }}</strong></div>
            <div>잔여 <strong class="text-h5">{{ mine.period.remaining_days }}일</strong></div>
            <div>승인 대기 <strong>{{ mine.period.pending_days }}일</strong></div>
            <div>신청 가능 <strong class="text-h5">{{ mine.period.available_days }}일</strong></div>
          </div>
          <VBtn
            variant="text"
            @click="showLedger(mine)"
          >
            부여·사용 이력
          </VBtn>
        </VCardText>
      </VCard>
      <VCard
        v-if="mine?.active && selectedSummer"
        :title="`${summerYear}년 여름휴가`"
        class="mb-4"
      >
        <VCardText>
          <p class="mb-3">
            사용 기간: {{ selectedSummer.starts_on }} ~ {{ selectedSummer.ends_on }} · 연차와 별도 · 이월 없음
          </p>
          <VAlert
            v-if="outsideSummerSeason"
            type="info"
            variant="tonal"
            class="mb-3"
          >
            지금은 여름휴가 사용 시즌이 아닙니다. 다음 여름의 7~8월 날짜를 미리 예약할 수 있습니다.
          </VAlert>
          <div class="d-flex flex-wrap ga-6 mb-3">
            <div>부여 <strong>{{ selectedSummer.granted_days }}일</strong></div>
            <div>사용 <strong>{{ selectedSummer.used_days }}일</strong></div>
            <div>잔여 <strong>{{ selectedSummer.remaining_days }}일</strong></div>
            <div>승인 대기 <strong>{{ selectedSummer.pending_days }}일</strong></div>
            <div>신청 가능 <strong>{{ selectedSummer.available_days }}일</strong></div>
          </div>
          <VBtn
            variant="text"
            @click="showLedger(mine, 'summer')"
          >
            여름휴가 이력
          </VBtn>
        </VCardText>
      </VCard>
      <VAlert
        v-if="mine?.period && !mine.can_apply"
        type="info"
        class="mb-4"
      >
        잔액은 반영되었습니다. 신청하려면 HR 담당자가 직원 수정에서 지정 승인자를 선택해야 합니다.
      </VAlert>
      <VCard
        v-if="mine?.can_apply && (mine.period || mine.summer)"
        title="휴가 신청"
        class="mb-4"
      >
        <VCardText>
          <VForm @submit.prevent="mutate(() => applyLeave(form), () => { form.reason = '' }, '휴가를 신청했습니다.')">
            <VRow>
              <VCol
                cols="12"
                md="3"
              >
                <VSelect
                  v-model="form.category"
                  :items="categories"
                  label="휴가 구분"
                  @update:model-value="changeCategory"
                />
              </VCol>
              <VCol
                cols="12"
                md="3"
              >
                <VSelect
                  v-model="form.kind"
                  :items="kinds"
                  label="사용 단위"
                />
              </VCol>
              <VCol
                cols="12"
                md="3"
              >
                <VTextField
                  v-model="form.starts_on"
                  type="date"
                  label="시작일"
                  :min="requestMin"
                  :max="requestMax"
                  required
                />
              </VCol>
              <VCol
                cols="12"
                md="3"
              >
                <VTextField
                  v-model="form.ends_on"
                  type="date"
                  label="종료일"
                  :min="form.starts_on"
                  :max="requestMax"
                  required
                />
              </VCol>
              <VCol
                cols="12"
                md="3"
              >
                <VBtn
                  type="submit"
                  :loading="busy"
                  class="mt-2"
                >
                  신청
                </VBtn>
              </VCol>
              <VCol cols="12">
                <VTextarea
                  v-model="form.reason"
                  label="사유 (선택, 승인자와 인사담당자만 열람)"
                  rows="2"
                  maxlength="1000"
                />
              </VCol>
            </VRow>
            <p class="text-body-2">
              월~금 중 등록된 휴무일을 제외하고 계산합니다. 반차는 시작일과 종료일을 같게 입력하세요.
            </p>
          </VForm>
        </VCardText>
      </VCard>
      <VCard
        class="mb-4"
        title="휴가 신청 내역"
      >
        <VTabs
          :model-value="scope"
          @update:model-value="changeScope(String($event))"
        >
          <VTab value="mine">
            내 신청
          </VTab><VTab value="approvals">
            승인할 신청
          </VTab><VTab
            v-if="overview.is_admin"
            value="all"
          >
            전체 신청
          </VTab>
        </VTabs>
        <VTable class="leave-table">
          <thead><tr><th>직원</th><th>기간</th><th>종류·일수</th><th>상태</th><th>사유·처리 메모</th><th>처리</th></tr></thead>
          <tbody>
            <tr
              v-for="request in requests.data"
              :key="request.id"
            >
              <td>{{ request.employee_name }}</td><td>{{ request.starts_on }} ~ {{ request.ends_on }}</td>
              <td>{{ request.category === 'summer' ? '여름휴가' : '연차' }} · {{ request.kind === 'annual' ? '종일' : kindNames[request.kind] }} · {{ request.days }}일</td>
              <td>
                <VChip size="small">
                  {{ statusNames[request.status] }}
                </VChip>
              </td>
              <td style="max-inline-size: 240px; white-space: normal">
                {{ request.reason || '—' }}<div
                  v-if="request.decision_note"
                  class="text-caption"
                >
                  {{ request.decision_note }}
                </div>
              </td>
              <td>
                <template v-if="request.employee_id === mine?.id">
                  <VBtn
                    v-if="request.status === 'pending' || (request.status === 'approved' && request.starts_on >= overview.today)"
                    size="small"
                    variant="text"
                    :disabled="busy"
                    @click="decide(request, 'cancel')"
                  >
                    취소 요청
                  </VBtn>
                </template>
                <template v-else-if="scope !== 'mine'">
                  <template v-if="request.status === 'pending'">
                    <VBtn
                      size="small"
                      variant="text"
                      :disabled="busy"
                      @click="decide(request, 'approve')"
                    >
                      승인
                    </VBtn>
                    <VBtn
                      size="small"
                      variant="text"
                      color="error"
                      :disabled="busy"
                      @click="decide(request, 'reject')"
                    >
                      반려
                    </VBtn>
                  </template>
                  <template v-if="request.status === 'cancellation_pending'">
                    <VBtn
                      size="small"
                      variant="text"
                      :disabled="busy"
                      @click="decide(request, 'approve_cancel')"
                    >
                      취소 승인
                    </VBtn>
                    <VBtn
                      size="small"
                      variant="text"
                      color="error"
                      :disabled="busy"
                      @click="decide(request, 'reject_cancel')"
                    >
                      취소 반려
                    </VBtn>
                  </template>
                </template>
              </td>
            </tr>
            <tr v-if="!requests.data.length">
              <td
                colspan="6"
                class="text-center pa-6"
              >
                신청 내역이 없습니다.
              </td>
            </tr>
          </tbody>
        </VTable>
        <VPagination
          v-model="page"
          :length="requests.last_page"
          :total-visible="5"
          @update:model-value="refreshRequests"
        />
      </VCard>
      <VCard
        v-if="overview.is_admin"
        title="직원 관리"
        class="mb-4"
      >
        <VCardText>
          <VAlert
            type="info"
            variant="tonal"
            class="mb-4"
          >
            입사일 기준으로 당기 부여·사용·잔액을 관리합니다. 연간 부여일수 변경은 다음 갱신부터 적용됩니다.
          </VAlert>
          <VBtn
            class="mb-3"
            @click="openEmployee()"
          >
            직원 추가
          </VBtn>
          <VSwitch
            v-model="showArchived"
            label="비활성 직원 포함"
            hide-details
            class="mb-3"
          />
          <VTable class="leave-table">
            <thead><tr><th>직원</th><th>입사일</th><th>당기 연간 부여</th><th>사용</th><th>현재 잔액</th><th>여름휴가</th><th>상태</th><th>관리</th></tr></thead>
            <tbody>
              <tr
                v-for="employee in visibleEmployees"
                :key="employee.id"
              >
                <td>{{ employee.name }}</td><td>{{ employee.hire_date }}</td>
                <td>{{ employee.period?.granted_days === null || !employee.period ? '미설정' : `${employee.period.granted_days}일` }}</td>
                <td>{{ employee.period?.used_days === null || !employee.period ? '미확정' : `${employee.period.used_days}일` }}</td>
                <td>{{ employee.period ? `${employee.period.remaining_days}일` : '미확정' }}</td>
                <td>{{ employee.summer.starts_on.slice(0, 4) }}년 · {{ employee.summer.available_days }}일</td>
                <td>{{ employee.archived_at ? '비활성 · 보관' : employee.active ? (employee.can_apply ? '활성' : '승인자 지정 필요') : '준비 중' }}</td>
                <td>
                  <VBtn
                    v-if="!employee.archived_at"
                    size="small"
                    variant="text"
                    @click="openEmployee(employee)"
                  >
                    수정
                  </VBtn><VBtn
                    v-if="employee.period && !employee.archived_at"
                    size="small"
                    variant="text"
                    @click="openAdjustment(employee)"
                  >
                    잔액 조정
                  </VBtn><VBtn
                    size="small"
                    variant="text"
                    @click="showLedger(employee)"
                  >
                    연차 이력
                  </VBtn>
                  <VBtn
                    size="small"
                    variant="text"
                    @click="showLedger(employee, 'summer')"
                  >
                    여름휴가 이력
                  </VBtn>
                  <VBtn
                    size="small"
                    variant="text"
                    @click="showEmployeeHistory(employee)"
                  >
                    변경 이력
                  </VBtn>
                  <VBtn
                    size="small"
                    variant="text"
                    :color="employee.archived_at ? 'primary' : 'warning'"
                    @click="openStatus(employee)"
                  >
                    {{ employee.archived_at ? '복원' : '비활성화' }}
                  </VBtn>
                </td>
              </tr>
            </tbody>
          </VTable>
        </VCardText>
      </VCard>
      <VCard
        title="등록된 휴무일"
        class="mb-4"
      >
        <VCardText>
          <VForm
            v-if="overview.is_admin"
            @submit.prevent="mutate(() => saveHoliday(holidayDate, holidayName), () => { holidayName = '' }, '휴무일을 등록했습니다.')"
          >
            <VRow>
              <VCol
                cols="12"
                md="4"
              >
                <VTextField
                  v-model="holidayDate"
                  type="date"
                  label="날짜"
                  :min="overview.today"
                  required
                />
              </VCol><VCol
                cols="12"
                md="4"
              >
                <VTextField
                  v-model="holidayName"
                  label="휴무일 이름"
                  required
                />
              </VCol><VCol
                cols="12"
                md="4"
              >
                <VBtn
                  type="submit"
                  :loading="busy"
                  class="mt-2"
                >
                  등록
                </VBtn>
              </VCol>
            </VRow>
          </VForm>
          <p v-if="!overview.holidays.length">
            등록된 휴무일이 없습니다.
          </p>
          <div
            v-for="holiday in overview.holidays"
            :key="holiday.id"
            class="d-flex align-center ga-3 mb-1"
          >
            <span>{{ holiday.date }} · {{ holiday.name }}</span><VBtn
              v-if="overview.is_admin"
              size="small"
              variant="text"
              color="error"
              :disabled="busy"
              @click="mutate(() => saveHoliday(holiday.date, holiday.name, true), () => {}, '휴무일을 삭제했습니다.')"
            >
              삭제
            </VBtn>
          </div>
        </VCardText>
      </VCard>
    </template>
    <VDialog
      v-model="employeeDialog"
      max-width="700"
      :persistent="busy"
    >
      <VCard :title="employeeId === null ? '직원 추가' : '직원 수정'">
        <VCardText>
          <VForm @submit.prevent="submitEmployee">
            <VAlert
              v-if="error"
              type="error"
              class="mb-4"
            >
              {{ error }}
            </VAlert>
            <VAlert
              v-if="initialized"
              type="info"
              class="mb-4"
            >
              계정·입사일·기준 잔액은 이력 보존을 위해 변경할 수 없습니다.
            </VAlert>
            <VRow>
              <VCol
                cols="12"
                md="6"
              >
                <VTextField
                  v-model="employeeForm.name"
                  label="이름"
                  required
                />
              </VCol>
              <VCol
                cols="12"
                md="6"
              >
                <VTextField
                  v-model="employeeForm.hire_date"
                  type="date"
                  label="입사일"
                  :max="overview?.today"
                  :disabled="initialized"
                  required
                />
              </VCol>
              <VCol
                cols="12"
                md="6"
              >
                <VSelect
                  v-model="employeeForm.user_id"
                  :items="accountItems"
                  label="직원 계정"
                  clearable
                  :disabled="initialized"
                />
              </VCol>
              <VCol
                cols="12"
                md="6"
              >
                <VSelect
                  v-model="employeeForm.approver_user_id"
                  :items="accountItems.filter(item => item.value !== employeeForm.user_id)"
                  :label="overview?.approval_pool ? '승인자 계정 (비우면 HR 공동 승인)' : '승인자 계정'"
                  clearable
                />
              </VCol>
              <VCol
                cols="12"
                md="6"
              >
                <VTextField
                  v-model.number="employeeForm.annual_days"
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  label="다음 갱신 부여일수 (미정이면 비워두세요)"
                />
              </VCol>
              <VCol
                cols="12"
                md="6"
              >
                <VTextField
                  v-model.number="employeeForm.summer_days"
                  type="number"
                  min="0"
                  max="3"
                  step="0.5"
                  label="새 여름 기간 부여일수"
                  hint="기본 3일. 기존 기간의 부여·사용 내역은 유지됩니다."
                  persistent-hint
                  required
                />
              </VCol>
              <VCol
                cols="12"
                md="6"
              >
                <VTextField
                  v-model="employeeForm.balance_date"
                  type="date"
                  label="잔액 기준일"
                  :max="overview?.today"
                  :disabled="initialized"
                />
              </VCol>
              <VCol
                cols="12"
                md="6"
              >
                <VTextField
                  v-model.number="employeeForm.opening_days"
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  label="입력할 초기 잔여 일수"
                  :disabled="initialized"
                />
              </VCol>
              <VCol
                cols="12"
                md="6"
              >
                <VSwitch
                  v-model="employeeForm.active"
                  label="확인을 마치고 신청 활성화"
                />
              </VCol>
            </VRow>
            <div class="d-flex justify-end ga-3">
              <VBtn
                variant="text"
                :disabled="busy"
                @click="employeeDialog = false"
              >
                취소
              </VBtn><VBtn
                type="submit"
                :loading="busy"
              >
                {{ employeeId === null ? '추가' : '수정 저장' }}
              </VBtn>
            </div>
          </VForm>
        </VCardText>
      </VCard>
    </VDialog>
    <VDialog
      v-model="statusDialog"
      max-width="540"
      :persistent="busy"
    >
      <VCard :title="statusEmployee?.archived_at ? '직원 복원' : '직원 비활성화'">
        <VCardText>
          <VForm @submit.prevent="confirmStatus">
            <p class="mb-4">
              {{ statusEmployee?.name }}{{ statusEmployee?.archived_at ? '님을 준비 상태로 복원합니다. 신청 활성화는 수정 화면에서 별도로 확인하세요.' : '님의 새 신청을 중지하고 기본 목록에서 숨깁니다. 계정·잔액·휴가 이력은 보존되며 다시 복원할 수 있습니다.' }}
            </p>
            <VAlert
              v-if="error"
              type="error"
              class="mb-4"
            >
              {{ error }}
            </VAlert>
            <VTextarea
              v-model="statusReason"
              label="처리 사유"
              required
              maxlength="1000"
            />
            <div class="d-flex justify-end ga-3">
              <VBtn
                variant="text"
                :disabled="busy"
                @click="statusDialog = false"
              >
                취소
              </VBtn>
              <VBtn
                type="submit"
                :loading="busy"
                :disabled="!statusReason.trim()"
              >
                {{ statusEmployee?.archived_at ? '준비 상태로 복원' : '비활성화' }}
              </VBtn>
            </div>
          </VForm>
        </VCardText>
      </VCard>
    </VDialog>
    <VDialog
      v-model="employeeHistoryDialog"
      max-width="800"
    >
      <VCard :title="`${historyEmployee?.name ?? ''} · 직원 변경 이력`">
        <VCardText>
          <p
            v-if="!employeeHistory.data.length"
            class="mb-4"
          >
            기록된 변경이 없습니다. 이 기능 도입 이후의 변경부터 기록합니다.
          </p>
          <div
            v-for="audit in employeeHistory.data"
            :key="audit.id"
            class="mb-4"
          >
            <strong>{{ auditNames[audit.action] || audit.action }}</strong> · {{ audit.actor_name || (audit.action === 'import' ? '시스템 이관' : '삭제된 관리자 계정') }} · {{ audit.created_at }}
            <p v-if="audit.reason">
              사유: {{ audit.reason }}
            </p>
            <div
              v-for="[field, value] in changedAuditFields(audit)"
              :key="field"
              class="text-body-2"
            >
              {{ fieldNames[field] || field }}: {{ formatAuditValue(field, audit.before_values?.[field]) }} → {{ formatAuditValue(field, value) }}
            </div>
          </div>
          <VPagination
            v-if="employeeHistory.last_page > 1"
            v-model="historyPage"
            :length="employeeHistory.last_page"
            @update:model-value="fetchEmployeeHistory"
          />
          <VBtn
            variant="text"
            @click="employeeHistoryDialog = false"
          >
            닫기
          </VBtn>
        </VCardText>
      </VCard>
    </VDialog>
    <VDialog
      v-model="decisionDialog"
      max-width="500"
      :persistent="busy"
    >
      <VCard :title="actionNames[selectedAction]">
        <VCardText>
          <p class="mb-3">
            {{ selectedRequest?.employee_name }} · {{ selectedRequest?.starts_on }} ~ {{ selectedRequest?.ends_on }}
          </p>
          <VTextarea
            v-model="decisionNote"
            label="처리 메모 (선택)"
            maxlength="1000"
          />
          <div class="d-flex justify-end ga-3">
            <VBtn
              variant="text"
              :disabled="busy"
              @click="decisionDialog = false"
            >
              닫기
            </VBtn><VBtn
              :loading="busy"
              @click="confirmDecision"
            >
              {{ actionNames[selectedAction] }}
            </VBtn>
          </div>
        </VCardText>
      </VCard>
    </VDialog>
    <VDialog
      v-model="adjustmentDialog"
      max-width="500"
      :persistent="busy"
    >
      <VCard :title="`${adjustmentEmployee?.name} 잔액 조정`">
        <VCardText>
          <VForm @submit.prevent="confirmAdjustment">
            <VTextField
              v-model.number="adjustmentDays"
              type="number"
              step="0.5"
              min="-100"
              max="100"
              label="증감 일수 (차감은 음수)"
              required
            />
            <VTextarea
              v-model="adjustmentNote"
              label="조정 사유"
              maxlength="1000"
              required
            />
            <div class="d-flex justify-end ga-3">
              <VBtn
                variant="text"
                :disabled="busy"
                @click="adjustmentDialog = false"
              >
                취소
              </VBtn><VBtn
                type="submit"
                :loading="busy"
              >
                조정 기록
              </VBtn>
            </div>
          </VForm>
        </VCardText>
      </VCard>
    </VDialog>
    <VDialog
      v-model="ledgerDialog"
      max-width="800"
    >
      <VCard :title="`${ledgerEmployee?.name} ${ledgerCategory === 'summer' ? '여름휴가' : '연차'} 변경 이력`">
        <VCardText>
          <VTable class="leave-table">
            <thead><tr><th>기록 시각</th><th>기간 번호</th><th>구분</th><th>증감</th><th>사유</th></tr></thead><tbody>
              <tr
                v-for="entry in ledger.data"
                :key="entry.id"
              >
                <td>{{ new Date(entry.created_at).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' }) }}</td><td>{{ entry.period_id }}</td><td>{{ (entry.kind === 'grant' && ledgerCategory === 'summer') ? '여름휴가 부여' : entryNames[entry.kind] }}</td><td>{{ entry.units / 2 }}일</td><td>{{ entry.note }}</td>
              </tr><tr v-if="!ledger.data.length">
                <td colspan="5">
                  기록이 없습니다.
                </td>
              </tr>
            </tbody>
          </VTable>
          <VPagination
            v-model="ledgerPage"
            :length="ledger.last_page"
            :total-visible="5"
            @update:model-value="fetchLedger"
          />
          <VBtn
            variant="text"
            @click="ledgerDialog = false"
          >
            닫기
          </VBtn>
        </VCardText>
      </VCard>
    </VDialog>
  </div>
</template>

<style scoped>
.leave-table :deep(table) {
  min-inline-size: 720px;
}

.leave-table :deep(th),
.leave-table :deep(td) {
  white-space: nowrap;
}
</style>
